import { useEffect, useRef, useState } from 'react';

export interface ConfirmDialogState {
  title: string;
  message: string;
  confirmLabel: string;
  risk?: 'medium' | 'high';
  requireText?: string;
  detailLabel?: string;
  detailValue?: string;
  detailPlaceholder?: string;
  detailRequired?: boolean;
  onDetailChange?: (value: string) => void;
  onConfirm: () => Promise<void> | void;
}

export function ConfirmDialog({
  state,
  onClose,
}: {
  state: ConfirmDialogState | null;
  onClose: () => void;
}) {
  const [typed, setTyped] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLElement | null>(null);
  const onCloseRef = useRef(onClose);
  const dialogKey = state ? `${state.title}|${state.message}|${state.confirmLabel}` : '';

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    setTyped('');
    setActionError(null);
    setIsSubmitting(false);
  }, [dialogKey]);

  useEffect(() => {
    if (!state) return undefined;

    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusable = readFocusableElements(dialogRef.current);
    focusable[0]?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && !isSubmitting) {
        event.preventDefault();
        onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab') return;

      const elements = readFocusableElements(dialogRef.current);
      if (elements.length === 0) return;
      const first = elements[0];
      const last = elements[elements.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previousFocus?.focus();
    };
  }, [dialogKey, isSubmitting, Boolean(state)]);

  if (!state) return null;

  const canConfirm = (!state.requireText || typed === state.requireText) && (!state.detailRequired || Boolean(state.detailValue?.trim()));

  async function confirm() {
    if (!state || !canConfirm) return;
    setIsSubmitting(true);
    setActionError(null);
    try {
      await state.onConfirm();
      setTyped('');
      onClose();
    } catch (caught) {
      setActionError(caught instanceof Error ? caught.message : 'Unable to complete this action.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <section ref={dialogRef} className="modal-dialog" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
        <div className="panel-header">
          <div>
            <h2 id="confirm-title">{state.title}</h2>
            <p>{state.message}</p>
          </div>
          <span className={`risk-pill risk-${state.risk ?? 'medium'}`}>{state.risk === 'high' ? 'High impact' : 'Controlled action'}</span>
        </div>
        {state.requireText ? (
          <label className="confirm-field">
            <span>Type {state.requireText} to continue</span>
            <input value={typed} onChange={(event) => setTyped(event.target.value)} />
          </label>
        ) : null}
        {state.detailLabel ? (
          <label className="confirm-field">
            <span>{state.detailLabel}</span>
            <textarea value={state.detailValue ?? ''} placeholder={state.detailPlaceholder} onChange={(event) => state.onDetailChange?.(event.target.value)} />
          </label>
        ) : null}
        {actionError ? <div className="form-error">{actionError}</div> : null}
        <div className="panel-actions">
          <button type="button" className="ghost-button" disabled={isSubmitting} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="danger-button" disabled={!canConfirm || isSubmitting} onClick={() => void confirm()}>
            {isSubmitting ? 'Working...' : state.confirmLabel}
          </button>
        </div>
      </section>
    </div>
  );
}

function readFocusableElements(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(
    'button:not(:disabled), [href], input:not(:disabled), textarea:not(:disabled), select:not(:disabled), [tabindex]:not([tabindex="-1"])',
  ));
}
